import { useState, useEffect, useRef } from "react"
import { MoreHorizontal, MapPin, X as XIcon, Heart, Plus, ArrowUpRight, Link2 } from "lucide-react"
import toast from "react-hot-toast"

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"
const BRANDFETCH_CLIENT_ID = "1idn4glWklGs5yLvUdW"

export const PASTEL_PALETTE = [
  "#FFECD4",
  "#EDE9FE",
  "#D0FAE5",
  "#FEE4E6",
  "#E0F2FE",
  "#FEF3C6",
]
const pastelAt = (i = 0) => PASTEL_PALETTE[((i % PASTEL_PALETTE.length) + PASTEL_PALETTE.length) % PASTEL_PALETTE.length]

// ── Exported helpers ─────────────────────────────────────────────
export function deriveLevel(job) {
  return job?.normalization?.seniority_key || "UNKNOWN"
}
export function parseSalary(raw) {
  if (!raw) return null
  return raw.replace(/[\n\t\r]+/g, " ").replace(/\s{2,}/g, " ")
    .replace(/-/g, "–").replace(/\s*\(.*$/s, "").trim()
}
export function daysAgoLabel(job) {
  let ts = null
  if (job?.posted_at_ts) ts = job.posted_at_ts * 1000
  else if (job?.posted_at) ts = new Date(job.posted_at).getTime()
  else if (job?.scraped_at) ts = new Date(job.scraped_at).getTime()
  if (!ts || isNaN(ts)) return ""
  const diffMs = Date.now() - ts
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffDays < 0) return "Just now"
  if (diffDays === 0) {
    const hrs = Math.floor(diffMs / 3600000)
    if (hrs < 1) { const mins = Math.floor(diffMs / 60000); return mins < 1 ? "Just now" : `${mins}m ago` }
    return `${hrs}h ago`
  }
  if (diffDays === 1) return "1 day ago"
  if (diffDays < 30) return `${diffDays} days ago`
  if (diffDays < 60) return "1 month ago"
  return `${Math.floor(diffDays / 30)} months ago`
}
export function initials(name = "") {
  return name.split(/[\s_\-.]+/).filter(Boolean).map(w => w[0]).join("").slice(0, 2).toUpperCase() || "??"
}

function stripHtml(html = "") {
  if (!html) return ""
  const tmp = document.createElement("div")
  tmp.innerHTML = html
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim()
}

// ── Company Logo ─────────────────────────────────────────────────
function CompanyLogo({ logoUrl, companyName, size = 44 }) {
  const [bfErr, setBfErr] = useState(false)
  const [fbErr, setFbErr] = useState(false)
  const domain = companyName
    ? companyName.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "") + ".com"
    : null
  const bfUrl = domain ? `https://cdn.brandfetch.io/${domain}?c=${BRANDFETCH_CLIENT_ID}` : null
  const src   = !bfErr ? bfUrl : (!fbErr && logoUrl ? logoUrl : null)
  const onErr = () => { if (!bfErr) setBfErr(true); else setFbErr(true) }

  const base = {
    width: size, height: size, borderRadius: 4, flexShrink: 0,
    background: "#f4f4f5",
    border: "1px solid #eeeeee",
  }
  if (src) {
    return (
      <img src={src} alt={companyName} onError={onErr} loading="lazy"
        style={{ ...base, objectFit: "contain" }} />
    )
  }
  return (
    <div style={{ ...base, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ fontSize: size * 0.36, fontWeight: 700, color: "#18181b", fontFamily: FF }}>
        {initials(companyName)}
      </span>
    </div>
  )
}

// ── Chip ─────────────────────────────────────────────────────────
function Chip({ label }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "5px 11px", borderRadius: 3,
      background: "#f4f4f5",
      fontSize: 11.5, fontWeight: 500, color: "#52525b",
      fontFamily: FF, whiteSpace: "nowrap", maxWidth: 160,
      overflow: "hidden", textOverflow: "ellipsis",
    }}>
      {label}
    </span>
  )
}

// ════════════════════════════════════════════
// CARD
// ════════════════════════════════════════════
export default function JobCard({
  job, onClick,
  savedJobIds, onHeartToggle, onApply,
  onPass,
  index = 0,
}) {
  const bg = pastelAt(index)
  const [saved,   setSaved]   = useState(savedJobIds?.has(job?.id || job?.job_id) ?? false)
  const [liking,  setLiking]  = useState(false)
  const [hov,     setHov]     = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    setSaved(savedJobIds?.has(job?.id || job?.job_id) ?? false)
  }, [savedJobIds, job])

  useEffect(() => {
    if (!menuOpen) return
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false) }
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false) }
    document.addEventListener("mousedown", onDoc)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDoc)
      document.removeEventListener("keydown", onKey)
    }
  }, [menuOpen])

  const handleCopyUrl = async (e) => {
    e.stopPropagation()
    const id = job?.id || job?.job_id
    if (!id) return
    const base = typeof window !== "undefined" ? window.location.origin : ""
    const url = `${base}/dashboard/jobs/${id}`
    try {
      await navigator.clipboard.writeText(url)
      toast.success("Link copied")
    } catch {
      toast.error("Could not copy link")
    }
    setMenuOpen(false)
  }

  const company   = job.company || "—"
  const title     = job.role || "—"
  const location  = job.canonical_location_id || job.location || null
  const postedAgo = daysAgoLabel(job)
  const summary   = stripHtml(job.description || "").slice(0, 180)

  // Tag chips — prefer explicit skills/tags, fall back to source or level words
  const skills = Array.isArray(job.skills) ? job.skills : []
  const tags = (skills.length > 0 ? skills : []).slice(0, 3)

  const handleSave = (e) => {
    e.stopPropagation()
    if (liking) return
    if (saved) { setSaved(false); onHeartToggle?.(job); return }
    setLiking(true)
    setTimeout(() => { setLiking(false); setSaved(true); onHeartToggle?.(job) }, 300)
  }

  const handlePass = (e) => {
    e.stopPropagation()
    onPass?.(job)
  }

  return (
    <div
      onClick={() => onClick?.(job)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", flexDirection: "column",
        background: "#ffffff",
        borderRadius: 14,
        border: "1px solid rgba(0,0,0,0.04)",
        padding: 5,
        cursor: "pointer",
        transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
        transform: hov ? "translateY(-2px)" : "translateY(0)",
        boxShadow: hov
          ? "0 10px 28px rgba(0,0,0,0.08)"
          : "0 1px 2px rgba(0,0,0,0.04)",
        fontFamily: FF,
        minHeight: 300,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Pastel content panel */}
      <div style={{
        display: "flex", flexDirection: "column",
        background: bg,
        borderRadius: 10,
        padding: 20,
        gap: 14,
        flex: 1,
      }}>
      {/* ── Header: logo + title/company + ⋯ ── */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <CompanyLogo logoUrl={job.company_logo} companyName={company} size={52} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{
            margin: 0, fontSize: 15.5, fontWeight: 700,
            lineHeight: 1.25, letterSpacing: "-0.015em",
            color: "#18181b", fontFamily: FF,
            display: "-webkit-box",
            WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {title}
          </h3>
          <p style={{
            margin: "4px 0 0", fontSize: 11, fontWeight: 600,
            letterSpacing: "0.08em", textTransform: "uppercase",
            color: "#a1a1aa", fontFamily: FF,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {company}
          </p>
        </div>
        <div ref={menuRef} style={{ position: "relative", flexShrink: 0 }}>
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v) }}
            aria-label="More"
            aria-expanded={menuOpen}
            style={{
              width: 26, height: 26, borderRadius: 4,
              background: menuOpen ? "#f4f4f5" : "none",
              border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: menuOpen ? "#18181b" : "#a1a1aa",
              transition: "background 0.12s, color 0.12s",
            }}
            onMouseEnter={e => { if (!menuOpen) { e.currentTarget.style.background = "#f4f4f5"; e.currentTarget.style.color = "#18181b" } }}
            onMouseLeave={e => { if (!menuOpen) { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#a1a1aa" } }}
          >
            <MoreHorizontal size={16} strokeWidth={2} />
          </button>
          {menuOpen && (
            <div
              role="menu"
              onClick={e => e.stopPropagation()}
              style={{
                position: "absolute",
                top: "calc(100% + 6px)", right: 0,
                minWidth: 160,
                background: "#ffffff",
                border: "1px solid #ececec",
                borderRadius: 10,
                boxShadow: "0 10px 32px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04)",
                padding: 4,
                zIndex: 20,
                fontFamily: FF,
              }}
            >
              <button
                role="menuitem"
                onClick={handleCopyUrl}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  width: "100%", padding: "8px 10px", borderRadius: 6,
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 12.5, fontWeight: 500, color: "#18181b",
                  fontFamily: FF, textAlign: "left",
                  transition: "background 0.1s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f6f6f7"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                <Link2 size={13} strokeWidth={2.2} />
                Copy URL
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Location ── */}
      {location && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <MapPin size={13} strokeWidth={2} color="#52525b" />
          <span style={{
            fontSize: 11.5, fontWeight: 600, letterSpacing: "0.06em",
            textTransform: "uppercase", color: "#18181b", fontFamily: FF,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {location}
          </span>
        </div>
      )}

      {/* ── Meta strip placeholder (space reserved, content removed) ── */}
      <div aria-hidden="true" style={{ height: 16 }} />

      {/* ── Summary ── */}
      {summary && (
        <p style={{
          margin: 0, fontSize: 12.5, color: "#52525b", fontFamily: FF,
          lineHeight: 1.55,
          display: "-webkit-box",
          WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {summary}
        </p>
      )}

      {/* ── Tags (reserved height even when empty) ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, minHeight: 22 }}>
        {tags.map((t, i) => <Chip key={i} label={t} />)}
      </div>

      {/* ── Posted ago (own row, above footer) ── */}
      {postedAgo && (
        <div style={{
          marginTop: "auto",
          fontSize: 11, fontWeight: 500,
          color: "#71717a", fontFamily: FF,
          letterSpacing: "0.01em",
        }}>
          {postedAgo}
        </div>
      )}
      </div>

      {/* ── Footer (white strip): save + pass (left) | Apply (right) ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "#ffffff",
        padding: "12px 14px",
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 2,
          background: "#f4f4f5",
          borderRadius: 10,
          padding: 3,
        }}>
          <button
            onClick={handleSave}
            aria-label={saved ? "Saved" : "Save"}
            style={{
              width: 28, height: 28, borderRadius: 7,
              background: saved ? "#ffffff" : "transparent",
              border: "none",
              cursor: liking ? "default" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: saved ? "#e11d48" : "#52525b",
              transition: "background 0.12s, color 0.12s",
            }}
            onMouseEnter={e => { if (!saved) { e.currentTarget.style.background = "#ffffff"; e.currentTarget.style.color = "#18181b" } }}
            onMouseLeave={e => { if (!saved) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#52525b" } }}
          >
            <Heart size={14} strokeWidth={2}
              fill={saved ? "#e11d48" : "none"}
              stroke={saved ? "#e11d48" : "currentColor"} />
          </button>
          <button
            onClick={handlePass}
            aria-label="Pass"
            style={{
              width: 28, height: 28, borderRadius: 7,
              background: "transparent", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#52525b", transition: "background 0.12s, color 0.12s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#ffffff"; e.currentTarget.style.color = "#18181b" }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#52525b" }}
          >
            <XIcon size={14} strokeWidth={2} />
          </button>
        </div>

        <button
          className="jc-apply"
          onClick={(e) => {
            e.stopPropagation()
            onApply?.(job)
          }}
        >
          <span>Apply</span>
          <ArrowUpRight size={14} strokeWidth={2.2} className="jc-apply-arrow" />
          <style>{`
            .jc-apply {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 6px;
              height: 34px;
              padding: 0 16px;
              background: #18181b;
              color: #ffffff;
              border: 1px solid #18181b;
              border-radius: 9999px;
              font-size: 12px;
              font-weight: 600;
              letter-spacing: -0.005em;
              font-family: ${FF};
              cursor: pointer;
              transition: background 0.2s ease, border-color 0.2s ease;
            }
            .jc-apply:hover { background: #000000; border-color: #000000; }
            .jc-apply-arrow { transition: transform 0.2s ease; }
            .jc-apply:hover .jc-apply-arrow { transform: translate(1px, -1px); }
          `}</style>
        </button>
      </div>
    </div>
  )
}

// ── Skeleton card (shimmer loading state) ───────────────────────
export function JobCardSkeleton() {
  return (
    <div
      className="jc-skel-card"
      style={{
        display: "flex", flexDirection: "column",
        background: "#ffffff",
        borderRadius: 14,
        border: "1px solid #f0f0f1",
        padding: 20,
        gap: 14,
        fontFamily: FF,
        minHeight: 300,
      }}
    >
      <style>{`
        @keyframes jc-skel-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .jc-skel {
          background: linear-gradient(90deg, #f0f0f1 0%, #f7f7f8 50%, #f0f0f1 100%);
          background-size: 200% 100%;
          animation: jc-skel-shimmer 1.4s ease-in-out infinite;
          border-radius: 4px;
        }
      `}</style>

      {/* Header: logo + title/company + ⋯ */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div className="jc-skel" style={{ width: 52, height: 52, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6, paddingTop: 2 }}>
          <div className="jc-skel" style={{ height: 14, width: "85%" }} />
          <div className="jc-skel" style={{ height: 14, width: "60%" }} />
          <div className="jc-skel" style={{ height: 10, width: "45%", marginTop: 2 }} />
        </div>
        <div className="jc-skel" style={{ width: 20, height: 20, flexShrink: 0 }} />
      </div>

      {/* Location */}
      <div className="jc-skel" style={{ height: 11, width: "40%" }} />

      {/* 3-col meta strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
        <div className="jc-skel" style={{ height: 12 }} />
        <div className="jc-skel" style={{ height: 12 }} />
        <div className="jc-skel" style={{ height: 12 }} />
      </div>

      {/* Summary (3 lines) */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div className="jc-skel" style={{ height: 10 }} />
        <div className="jc-skel" style={{ height: 10 }} />
        <div className="jc-skel" style={{ height: 10, width: "70%" }} />
      </div>

      {/* Tags */}
      <div style={{ display: "flex", gap: 6 }}>
        <div className="jc-skel" style={{ height: 22, width: 54, borderRadius: 3 }} />
        <div className="jc-skel" style={{ height: 22, width: 42, borderRadius: 3 }} />
        <div className="jc-skel" style={{ height: 22, width: 34, borderRadius: 3 }} />
      </div>

      {/* Footer */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginTop: "auto", paddingTop: 6,
      }}>
        <div style={{ display: "flex", gap: 4 }}>
          <div className="jc-skel" style={{ width: 32, height: 32 }} />
          <div className="jc-skel" style={{ width: 32, height: 32 }} />
        </div>
        <div className="jc-skel" style={{ width: 86, height: 32 }} />
      </div>
    </div>
  )
}

// ── Add-Your-Own-Link tile (first slot in the grid) ──────────────
export function AddLinkCard({ onClick, index = 0 }) {
  const [hov, setHov] = useState(false)
  const bg = pastelAt(index)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100%",
        width: "100%",
        padding: 5,
        textAlign: "left",
        background: "#ffffff",
        border: "1px solid #e4e4e7",
        borderRadius: 10,
        cursor: "pointer",
        fontFamily: FF,
        boxShadow: hov
          ? "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)"
          : "0 1px 2px 0 rgba(0,0,0,0.05)",
        transition: "box-shadow 0.3s ease",
      }}
    >
      {/* Top panel */}
      <div
        style={{
          position: "relative",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 220,
          padding: 24,
          borderRadius: 6,
          background: bg,
          transition: "background 0.2s ease",
        }}
      >
        {/* Row: label + icon */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#1f2937" }}>
              Workday, Greenhouse
            </span>
            <span style={{ fontSize: 12, color: "#6b7280" }}>etc.</span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 56,
              width: 56,
              borderRadius: 9999,
              background: "rgba(0,0,0,0.06)",
              transform: hov ? "scale(1.1)" : "scale(1)",
              transition: "transform 0.2s ease",
            }}
          >
            <Plus size={24} strokeWidth={2.25} color="#18181b" />
          </div>
        </div>

        {/* Headline */}
        <div
          style={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            justifyContent: "center",
            margin: "6px 0",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 400,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "#111827",
            }}
          >
            Add Your Own Link
          </h3>
        </div>

        {/* Caption */}
        <p
          style={{
            marginTop: "auto",
            marginBottom: 0,
            fontSize: 12,
            color: "#6b7280",
          }}
        >
          Paste a URL and optional job description
        </p>
      </div>

      {/* Bottom row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#ffffff",
          padding: "14px 16px",
        }}
      >
        <div style={{ minWidth: 0, flex: 1, marginRight: 14 }}>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 700,
              color: "#111827",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Quick Apply
          </p>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>
            Any supported job board
          </p>
        </div>
        <div
          style={{
            display: "inline-flex",
            flexShrink: 0,
            alignItems: "center",
            gap: 6,
            background: "#000",
            color: "#fff",
            borderRadius: 9999,
            padding: "9px 16px",
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          <Plus size={14} strokeWidth={2.5} />
          Add
        </div>
      </div>
    </button>
  )
}
